import { useEffect, useRef, useState } from 'react';
import { editingDraftKey, readEditingDraft, writeEditingDraft, discardEditingDraft } from '@/lib/operator-drafts';
/** Editing drafts are distinct from submitted records and never uploaded automatically. */
export function useOperatorDraft<T extends Record<string, string | boolean>>(scope: string | null, form: string, row: string, defaults: T) {
  const key = editingDraftKey(scope, form, row);
  const initial = useRef(defaults);
  const [fields, setFields] = useState<T>(defaults);
  const [photo, setPhoto] = useState<File | null>(null);
  const [readyKey, setReadyKey] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const writes = useRef(Promise.resolve());
  const currentKey = useRef(key); currentKey.current = key;
  const saved = useRef<string | null>(null);
  const revision = useRef(0);
  useEffect(() => {
    let cancelled = false;
    saved.current = null;
    setReadyKey(null);
    setStatus('');
    setFields(initial.current); setPhoto(null);
    void readEditingDraft(key).then(draft => {
      if (cancelled) return;
      if (draft) {
        setFields({ ...initial.current, ...draft.fields } as T);
        setPhoto(draft.photo ? new File([draft.photo], 'draft-photo.jpg', { type: draft.photo.type }) : null);
        setStatus('Draft restored on this device');
      }
      setReadyKey(key);
    }).catch(() => { if (!cancelled) setStatus('Draft storage unavailable. Keep this page open until you save.'); });
    return () => { cancelled = true; };
  }, [key]);
  const serialized = JSON.stringify(fields);
  useEffect(() => {
    if (!key || readyKey !== key || saved.current === key) return;
    const version = revision.current;
    const blank = serialized === JSON.stringify(initial.current) && !photo;
    writes.current = writes.current.catch(() => {}).then(async () => {
      if (blank) await discardEditingDraft(key);
      else await writeEditingDraft(key, JSON.parse(serialized), photo);
      if (currentKey.current === key && revision.current === version) setStatus(blank ? '' : 'Draft saved on this device');
    }).catch(() => { if (currentKey.current === key) setStatus('Draft not saved. Keep this page open and try again.'); });
  }, [key, readyKey, serialized, photo]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (status === 'Saving draft…' || status.startsWith('Draft not saved')) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [status]);
  const clear = async () => {
    if (!key) return;
    saved.current = key;
    await writes.current;
    await discardEditingDraft(key);
    if (currentKey.current === key) setStatus('');
  };
  const discard = async () => {
    await clear();
    setFields(initial.current); setPhoto(null);
    saved.current = null;
  };
  return { fields, setField: <K extends keyof T>(name: K, value: T[K]) => { revision.current++; setStatus('Saving draft…'); setFields(current => ({ ...current, [name]: value })); }, photo,
    setPhoto: (value: File | null) => { revision.current++; setStatus('Saving draft…'); setPhoto(value); }, status, clear, discard, ready: !!key && readyKey === key };

}
